import { EventEmitter, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InteractiveContent, QuestionAnswered, Questions, TutorDialogData, TutorType, UserAction, UserActionType, UserActionValue } from 'models';
import { SearchUtility } from 'utility';
import { TutorComponent } from './tutor/tutor.component';
import { environment } from 'src/environments/environment';
import { text } from '@fortawesome/fontawesome-svg-core';
import { TutorDialogTxtsService } from './tutor-dialog-txts.service';
import { SurveyUtility } from 'src/_utility/survey';
import { BrowserConfig } from 'src/_services';

@Injectable({
  providedIn: 'root'
})
export class TutorService {

  status = {
    isActive: false,
    isFinalTutorDone: false,
    isManual: false,
    type: undefined
  }

  nMax: number = BrowserConfig.nMax ?? 10; //Maximum number of policies for the optimal range
  nMin: number = BrowserConfig.nMin ?? 2; //Minimum number of policies for the optimal range
  nLimit: number = BrowserConfig.nLimit ?? 50; //Maximum number of policies that will be displayed as result
  
  private lastSuggestion: string; // Last question that has been suggested to be changed
  private nonFilteringQuestions: QuestionAnswered[]; // List of Questions that didn't filter any insurance
  private moreFilteringQuestions: QuestionAnswered[]; // List of Questions that have filtered more than BrowserConfig.nMax / 2

  /**
   * When the user interact with the dialog it will emit the user decision
   */
  userAction = new EventEmitter<UserAction>();

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    public dialog: MatDialog,
  ) { }

  reset() {
    this.status = {
      isActive: false,
      isFinalTutorDone: false,
      isManual: false,
      type: undefined
    }
  }

  //#region getTutorType Method
  getTutorType(nPolicies: number, isLast: boolean = false): TutorType {
    /**
     * OK
     * Il numero di prodotti si trova nel range ottimale
     */
    if (!isLast && nPolicies <= this.nMax && nPolicies >= this.nMin) { return TutorType.K }

    /**
     * POCHI
     * Il numero di prodotti è minore del numero minimo per il range ottimale
     */
    if (!isLast && nPolicies < this.nMin) { return TutorType.P }

    /**
     * TANTI
     * Se l'ultima del questionario è stato risposto ed 
     * è stato attivato il processo di revisione, ma il numero
     * delle polizze risulta comunque maggiore rispetto al 
     * numero massimo del range ottimale
     */
    if (isLast && this.status.isFinalTutorDone && nPolicies > this.nLimit) { return TutorType.T }

    /**
     * FINALE
     * Se l'ultima domanda del questionario è stato risposto e 
     * il numero dei prodotti è maggiore al numero massimo dei 
     * prodotti visualizzabili nel risultato
     */
    if (isLast && nPolicies > this.nLimit) { return TutorType.F }

    return undefined
  }
  //#endregion getTutorType Method

  //#region Questions to suggest for the tutor
  getNextNonFilteringQuestionId(): string {
    return this.nonFilteringQuestions?.length > 0 ? this.nonFilteringQuestions.pop()?.id : null;
  }

  getNextMoreFitleringQuestionId(): string {
    return this.moreFilteringQuestions?.length > 0 ? this.moreFilteringQuestions.pop()?.id : null;
  }
  //#endregion Questions to suggest for the tutor

  //#region insertAnswers Method
  async insertAnswers(questionsAnswered: QuestionAnswered[], questions: Questions, isLast: boolean): Promise<boolean | UserAction> {
    return new Promise<boolean | UserAction>(async (resolve, rejects) => {
      try {
        if (this.status.isActive && this.status.type == TutorType.P && (!this.moreFilteringQuestions || this.moreFilteringQuestions.length == 0)) {
          this.status.isActive = false;
        }
        if (this.status.isActive && this.status.type == TutorType.F && (!this.nonFilteringQuestions || this.nonFilteringQuestions.length == 0)) {
          this.status.isFinalTutorDone = true;
          this.status.isActive = false;
        }

        const lastQuestionAnswered = questionsAnswered[questionsAnswered.length - 1];
        const tutorType = this.getTutorType(lastQuestionAnswered.countPolicies, isLast);

        if (tutorType && (!this.status.isActive || this.status.type != tutorType)) {
          if (tutorType == TutorType.P) {
            this.moreFilteringQuestions = SearchUtility.getMoreFilteringQuestions(questionsAnswered);
          }
          if (tutorType == TutorType.F) {
            this.nonFilteringQuestions = SearchUtility.getNonFilteringQuestions(questionsAnswered);
          }

          const tutorDialogTxt = TutorDialogTxtsService[tutorType](lastQuestionAnswered.countPolicies);
          tutorDialogTxt.type = 'tutor';
          const res = await this.openDialog(tutorDialogTxt.data, tutorDialogTxt.uav);
          this.status.isActive = res.type == UserActionType.activateTutor;
          if (res.type == UserActionType.activateTutor && !res.isManual) {
            this.status.type = tutorType;
            let suggestQ = tutorType == TutorType.P ? this.getNextMoreFitleringQuestionId() : this.getNextNonFilteringQuestionId();
            res.questionId = suggestQ;
          }
          resolve(res);
        }
        else if (tutorType && this.status.isActive && !this.status.isManual) {
          /* Verifico se durante la modifica delle risposte, si siano attivati delle domande opzionali ed eventualmente suggerisco quelle domande */
          let suggestQ: string = undefined;
          const qToAnswer = SurveyUtility.getLastQuestionToAnswer(questions);
          if (qToAnswer && qToAnswer.nextQuestion && SurveyUtility.isQuestionAnswered(qToAnswer.nextQuestion)) {
            suggestQ = qToAnswer.id;
          }
          else {
            suggestQ = tutorType == TutorType.P ? this.getNextMoreFitleringQuestionId() : this.getNextNonFilteringQuestionId();
          }

          if(suggestQ) {
            resolve({type: UserActionType.activateTutor, isManual: false, questionId: suggestQ})
          }
        }
        else if (isLast) { 
          this.status.isActive = false;
          this.status.type = undefined;
          resolve({type: UserActionType.startSearch})
        }
        else {
          this.status.isActive = false;
          this.status.type = undefined;
          resolve(true);
        }
      }
      catch(err) { rejects(err); }
    })
  }
  //#endregion insertAnswers

  //#region showText Method
  /**
   * Show a simple text in dialog.
   * @param text Text to show
   * @param title Title of the dialog
   * @param withTextClose If true, it will add at the end "Clicca su OK per chiudere il suggerimento". Default false
   * @param moreContent Other content to insert after text (and before the "OK" text if exists) (can't be value = -10)
   * @param userActionEvent The relation between content.value and user action to emit on user action. (can't be value = -10)
   * @returns The id of the user action. 
   */
  showText(text: string, title: string = '', withTextClose = false, moreContent: InteractiveContent[], userActionEvent: UserActionValue[] = [], type: ('tutor' | 'info')) {

    return new Promise(async (resolve, rejects) => {
      try {
        /** Create the content and add some more content if passed */
        const data: TutorDialogData = {
          title,
          content: [
            {
              typo: 'str',
              str: text
            },
            ...moreContent
          ],
          type: type
        }


        /**
         * If with text close add a final standard curtesy string to close the dialog
         */
        if (withTextClose) {
          if (userActionEvent.find(f => f.value == -10)) throw '-10 value is reserved'
          userActionEvent.push({ action: { type: UserActionType.ignore }, value: -10 })
          data.content.push(
            {
              typo: 'break'
            },
            {
              typo: 'str',
              str: 'Clicca su'
            },
            {
              typo: 'click',
              str: 'OK',
              value: -10
            },
            {
              typo: 'str',
              str: 'per chiudere questo suggerimento'
            }
          )

        }

        const res = await this.openDialog(data, userActionEvent);

        resolve(res);
      } catch (e) { rejects(e) }
    })

  }
  //#endregion showText Method

  //#region openDialog Method
  /**
   * It opens the dialog
   * @param data Data to present in the dialog
   * @param userActionEvent  The relation between data.content[*].value and user action to emit on user action. (can't be value = -1)
   * @returns 
   */
  openDialog(data: TutorDialogData, userActionEvent: { action: UserAction, value: number }[] = []) {
    return new Promise<UserAction>((resolve, rejects) => {

      try {
        const content = data.content;

        /** Any of the content must have a -1 value event becouse is reserved */
        const arrEventsForbidden = [-1]
        if (content.find(f => 'value' in f && arrEventsForbidden.includes(f.value)) || userActionEvent.map(m => m.value).find(f => arrEventsForbidden.includes(f)))
          throw `${arrEventsForbidden} are forbidden value for value event`

        // not a prblem
        // if (!userActionEvent.every(uae => content.find(f => 'value' in f && f.value == uae.value) ? true : false))
        //   throw `not every values in userActionEvent are in moreContent`;

        /** check if every value of the content has a matching userAction in userActionEvent */
        if (!content.filter(fil => 'value' in fil).every(mc => userActionEvent.find(f => 'value' in mc && f.value == mc.value) ? true : false))
          throw `not every values of moreContent in are in userActionEvent`;

        /**
         * open the dialog
         */
        const dialogRef = this.dialog.open(TutorComponent, {
          data,
          // width: '600px',
          enterAnimationDuration: '1000ms',
          exitAnimationDuration: '500ms',
          disableClose: true
          // position: { top: '15px', right: '15px' }
        })

        /**
         * Subscribe to the after close event of the dialog. The user made an action.
         */
        dialogRef.afterClosed().subscribe(result => {
          /** if result id undefined it means that the user closed the dialog without clicking a string */
          const res = result ?? -1;

          const found = userActionEvent.find(f => f.value == res);
          const action = found ? found.action : ({ type: UserActionType.ignore } as UserAction);
          this.userAction.next(action)

          resolve(action)
        });
      } catch (e) {
        rejects(e);
      }
    })
  }
  //#endregion openDialog Method

}
